import type { FormInstance } from '@arco-design/web-react';
import get from 'lodash/get';

import { buildOptionMetaPatch } from '../../platform/option-meta';
import type { VendorDeviceSelection } from '../vendorAndDeviceRadioList';
import { GENERIC_VENDOR_DEVICE_LABEL, formatVendorDeviceCellLabel } from '../vendorAndDeviceRadioList/utils';
import type { VendorDeviceMeta } from './types';
import { DISK_PRICING_FIELD, VENDOR_DEVICE_FIELD_KEYS } from './types';

export function readVendorDeviceMeta(values: Record<string, unknown>, prefix: string): VendorDeviceMeta | undefined {
  const meta: VendorDeviceMeta = {};
  let hasAny = false;
  VENDOR_DEVICE_FIELD_KEYS.forEach((key) => {
    const value = get(values, `${prefix}.${key}`);
    if (value != null && value !== '') {
      meta[key] = String(value);
      hasAny = true;
    }
  });
  if (hasAny) return meta;

  const raw = get(values, prefix);
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  return raw as VendorDeviceMeta;
}

export function readDiskPricingMeta(formValues: Record<string, unknown>): VendorDeviceMeta | undefined {
  return readVendorDeviceMeta(formValues, DISK_PRICING_FIELD);
}

/**
 * 计价选中厂商设备后回填 storageClass：
 * - 提交值用 deviceID（写入 deliveryMetadata.storageClassName / storageclass）
 * - 展示名写入 optionMeta[deviceID]，UI 不直接用 vendor/device 名称当提交值
 */
export function syncPricingDeviceToStorageClass(
  form: FormInstance,
  selection: VendorDeviceSelection | null | undefined,
  storageClassField: string,
) {
  const deviceID = selection?.deviceID?.trim() || undefined;
  const vendorID = selection?.vendorID?.trim() || undefined;
  const deviceName = selection?.device?.trim() || undefined;
  const vendorName = selection?.vendor?.trim() || undefined;

  form.setFieldValue(storageClassField, deviceID);

  if (!deviceID) return;

  const formValues = form.getFieldsValue() as Record<string, unknown>;
  const displayName = deviceName || deviceID;
  const label = [vendorName, deviceName].filter(Boolean).join(' / ') || displayName;
  form.setFieldsValue(
    buildOptionMetaPatch(
      deviceID,
      {
        value: deviceID,
        name: label,
        label,
        displayName,
        vendor: vendorName,
        device: deviceName,
        vendorID,
        deviceID,
        billItemId: selection?.billItemId,
        quantityUnit: selection?.quantityUnit,
      },
      formValues,
    ),
  );
}

export function formatVendorDeviceLabel(meta?: VendorDeviceMeta): string {
  const vendorRaw = meta?.vendor?.trim();
  const deviceRaw = meta?.device?.trim();
  if (!vendorRaw && !deviceRaw) return GENERIC_VENDOR_DEVICE_LABEL;
  const vendor = vendorRaw ? formatVendorDeviceCellLabel(vendorRaw) : undefined;
  const device = deviceRaw ? formatVendorDeviceCellLabel(deviceRaw) : undefined;
  if (vendor && device) return `${vendor} / ${device}`;
  return vendor || device || GENERIC_VENDOR_DEVICE_LABEL;
}

export function vendorDeviceMetaFromSelection(selection?: VendorDeviceSelection | null): VendorDeviceMeta | undefined {
  if (!selection) return undefined;
  return normalizeVendorDeviceMeta({
    vendor: selection.vendor,
    device: selection.device,
    vendorID: selection.vendorID,
    deviceID: selection.deviceID,
    deviceType: selection.deviceType,
    billItemId: selection.billItemId,
    quantityUnit: selection.quantityUnit,
  });
}

/** 规范化厂商设备元数据，仅保留 VENDOR_DEVICE_FIELD_KEYS 中有值的字段 */
export function normalizeVendorDeviceMeta(meta?: VendorDeviceMeta | null): VendorDeviceMeta | undefined {
  if (!meta) return undefined;
  const normalized: VendorDeviceMeta = {};
  let hasAny = false;
  VENDOR_DEVICE_FIELD_KEYS.forEach((key) => {
    const value = meta[key];
    if (value != null && String(value).trim() !== '') {
      normalized[key] = String(value).trim();
      hasAny = true;
    }
  });
  return hasAny ? normalized : undefined;
}

/** 规范化计价 meta；保留 quantityUnit / billItemId，并可选带 requirement */
export function readPricingMeta(raw: unknown): (VendorDeviceMeta & { requirement?: string }) | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const item = raw as Record<string, unknown>;
  const meta = normalizeVendorDeviceMeta(item as VendorDeviceMeta);
  const requirement = item.requirement != null && String(item.requirement).trim() !== '' ? String(item.requirement) : undefined;
  if (!meta && !requirement) return undefined;
  return {
    ...meta,
    ...(requirement ? { requirement } : {}),
  };
}

/** 生成 innerForm 上 disk.pricing.* 的点路径 patch */
export function buildVendorDeviceMetaFormPatch(prefix: string, meta?: VendorDeviceMeta | null): Record<string, unknown> {
  const patch: Record<string, unknown> = { [prefix]: meta ?? undefined };
  VENDOR_DEVICE_FIELD_KEYS.forEach((key) => {
    patch[`${prefix}.${key}`] = meta?.[key];
  });
  return patch;
}

/** 将厂商设备字段同步到 innerForm（对象 + 各子字段） */
export function syncVendorDeviceMetaToInnerForm(form: FormInstance, prefix: string, meta?: VendorDeviceMeta | null) {
  form.setFieldsValue(buildVendorDeviceMetaFormPatch(prefix, normalizeVendorDeviceMeta(meta)));
}

/** 厂商设备元数据 watch 路径：前缀对象 + vendorID/deviceID 等叶子（Arco 仅 watch 父对象时叶子变更常不触发） */
export function buildVendorDeviceMetaWatchFields(prefix: string): string[] {
  return [prefix, ...VENDOR_DEVICE_FIELD_KEYS.map((key) => `${prefix}.${key}`)];
}

/** 磁盘类 pricing 子树 watch（diskPath.pricing.*） */
export function buildDiskPricingWatchFields(diskPath: string): string[] {
  return buildVendorDeviceMetaWatchFields(`${diskPath}.pricing`);
}
